import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { GridOptions } from 'ag-grid-community';
import { Observable, Subject } from 'rxjs';
import { UrlBuilder } from '../../../core';
import { CovanceGridColumn } from '../../../covance-ag-grid';
import { EQueryHideStorage } from './e-query-hide-storage.service';
import { UserPermissionsStorage } from '../../../user-permissions/user-permissions-storage';
import { EQueryAdminSummary } from '../models/e-query-admin-summary';
import { AdminEQueryHideColumns } from './admin-e-query-hide-columns';
import { map, publishReplay, refCount, startWith, switchMap } from 'rxjs/operators';
import { orderBy } from 'lodash';
import { AdminEQueryFilterDefinitionsResolver } from '../admin-e-query-filter-definitions-resolver';
import { CovanceFilter, FilterFieldValue } from '../../../covance-filter';
import { SearchFieldsValues } from './hide-filter/hide-equery-filter.component';

@Component({
    selector: 'admin-e-query-hide',
    template: require('./admin-e-query-hide.component.html'),
    styles: [require('./admin-e-query-hide.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminEQueryHideComponent {
    public readonly gridOptions: GridOptions = {
        floatingFilter: false,
        enableFilter: false,
        floatingFiltersHeight: 32,
        rowClassRules: {
            'hidden-query': ({ data }: { data: EQueryAdminSummary }) => data.isHidden
        }
    };
    public protocolControl: FormControl;
    public readonly columns$: Observable<CovanceGridColumn[]>;
    public eQueriesAdminSummaries$: Observable<EQueryAdminSummary[]>;
    public filterFields$: Observable<FilterFieldValue[]>;
    private readonly searchFieldValues$ = new Subject<SearchFieldsValues>();

    constructor(
        readonly urlBuilder: UrlBuilder,
        private readonly eQuerySummaryFilterResolver: AdminEQueryFilterDefinitionsResolver,
        readonly userPermissionsStorage: UserPermissionsStorage,
        private readonly eQueryHideUnhideStorage: EQueryHideStorage,
        private readonly adminEQueryHideUnhideColumns: AdminEQueryHideColumns,
        private readonly filterService: CovanceFilter
    ) {
        this.columns$ = this.adminEQueryHideUnhideColumns.columns$;

        const filterDefs = this.eQuerySummaryFilterResolver.createAdminHideUnhideFilterItems();

        const filterFields$ = this.searchFieldValues$.pipe(
            map(values => {
                this.filterService.setFiltersItems([], filterDefs);
                return { fields: filterDefs, values };
            }),
            publishReplay(1),
            refCount());
        
        this.filterFields$ = filterFields$.pipe(map(x => x.fields));
        
        const filterFlow$ = filterFields$
            .pipe(
                switchMap(({ fields, values }) => filterService.listen(fields).pipe(map(() => ({ fields, values })))),
                publishReplay(1),
                refCount()
            );

        this.eQueriesAdminSummaries$ = filterFlow$
            .pipe(
                switchMap(({ fields, values }) =>
                    this.eQueryHideUnhideStorage.getEQuerySummaries(values, fields).pipe(map(this.sortEQueries)),
                ),
                publishReplay(1),
                refCount(),
                startWith([]),
            );
    }

    public onSearch(values: SearchFieldsValues) {
        this.searchFieldValues$.next(values);
    }

    private sortEQueries = (eQueries: EQueryAdminSummary[]): EQueryAdminSummary[] | null => {
        if (!eQueries) {
            return null;
        }

        return orderBy<EQueryAdminSummary>(eQueries, eq => eq.eQueryId, 'desc');
    }
}
